import { useCallback, useEffect, useState } from 'react';
import axios from 'src/utils/axios';
import {
  isWithinLiveAgentHours as fallbackIsOpen,
  nextOpenLabel as fallbackNextOpenLabel,
} from 'src/utils/supportHours';

export interface SupportHoursStatus {
  isOpen: boolean;
  nextOpenLabel: string | null;
  /** True only during the very first fetch (no cached/fallback data shown yet). */
  loading: boolean;
}

interface ChatStatusResponse {
  enabled: boolean;
  is_open: boolean;
  next_open_label: string | null;
}

interface HoursResult {
  isOpen: boolean;
  nextOpenLabel: string | null;
}

/**
 * Live-agent hours, sourced from Thor's /connect/chat_status/ endpoint,
 * which reads Amazon Connect's Hours of Operation — the single source of
 * truth shared by both the voice and chat queues — cached 5 minutes
 * server-side. Editing hours in Connect takes effect here without a
 * frontend deploy.
 *
 * No client-side polling: the backend's 5-minute cache is the freshness
 * mechanism. This hook refetches on mount and on visibilitychange (catches
 * a page left open across an hours boundary), matching the interaction
 * pattern the old hardcoded isWithinLiveAgentHours() check used.
 *
 * Module-level cache: multiple mounted consumers (EscalateActions,
 * ChatLauncher) share one fetch/result rather than each hitting the
 * endpoint independently.
 */

let cachedResult: HoursResult | null = null;
let inflightFetch: Promise<HoursResult> | null = null;
const listeners = new Set<(result: HoursResult) => void>();

function localFallback(): HoursResult {
  const now = new Date();
  const isOpen = fallbackIsOpen(now);
  return { isOpen, nextOpenLabel: isOpen ? null : fallbackNextOpenLabel(now) };
}

function fetchStatus(): Promise<HoursResult> {
  if (inflightFetch) {
    return inflightFetch;
  }
  inflightFetch = axios
    .get<ChatStatusResponse>('/connect/chat_status/')
    .then((res) => ({
      isOpen: res.data.is_open,
      nextOpenLabel: res.data.next_open_label,
    }))
    .catch(() => localFallback())
    .then((result) => {
      cachedResult = result;
      listeners.forEach((listener) => listener(result));
      return result;
    })
    .finally(() => {
      inflightFetch = null;
    });
  return inflightFetch;
}

export function useSupportHours(): SupportHoursStatus {
  // Initial value: use the cached server result if another consumer already
  // fetched it (zero flicker); otherwise compute the local hardcoded
  // fallback synchronously so the first paint is a reasonable approximation
  // instead of an empty/loading state, then correct it once the real fetch
  // resolves (usually a no-op, since the fallback mirrors the real schedule
  // in the common case).
  const [result, setResult] = useState<HoursResult>(
    () => cachedResult ?? localFallback()
  );
  const [loading, setLoading] = useState(cachedResult === null);

  const refresh = useCallback(() => {
    fetchStatus().then((fresh) => {
      setResult(fresh);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();

    const listener = (fresh: HoursResult): void => {
      setResult(fresh);
    };
    listeners.add(listener);

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      listeners.delete(listener);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refresh]);

  return {
    isOpen: result.isOpen,
    nextOpenLabel: result.nextOpenLabel,
    loading,
  };
}

/**
 * Test-only: reset the module-level singleton cache between tests. The
 * singleton is intentional in production (shares one fetch across mounted
 * consumers), but that same sharing makes cross-test isolation via
 * vi.resetModules() unreliable — dynamic re-imports can race with a shared
 * worker's module registry. Call this in beforeEach instead.
 */
export function _resetSupportHoursCacheForTests(): void {
  cachedResult = null;
  inflightFetch = null;
  listeners.clear();
}
